<template>
    <v-chip :color="color" size="small" variant="tonal" class="version-chip">
        <v-icon v-if="icon" start :icon="icon" size="14" />
        <span class="text-data">{{ version.name }}</span>
    </v-chip>
</template>

<script setup lang="ts">
// Version chip colored by lifecycle state using the product-semantic theme
// colors: released -> "release" (gold), past freeze date -> "freeze"
// (glacial cyan), otherwise open -> primary.
import { computed } from "vue";
import type { ProjectVersion } from "@/api/projects";

type Props = {
    version: Pick<ProjectVersion, "name" | "freezeDate" | "releaseDate">;
};

const props = defineProps<Props>();

type VersionState = "released" | "frozen" | "open";

const state = computed<VersionState>(() => {
    if (props.version.releaseDate) return "released";
    if (props.version.freezeDate && props.version.freezeDate.getTime() <= Date.now()) {
        return "frozen";
    }
    return "open";
});

const color = computed(
    () => ({ released: "release", frozen: "freeze", open: "primary" })[state.value]
);

const icon = computed(
    () =>
        ({ released: "mdi-star-four-points", frozen: "mdi-snowflake", open: undefined })[
            state.value
        ]
);
</script>

<style lang="scss">
.version-chip .text-data {
    font-size: 0.95em;
    font-weight: 600;
}
</style>
